import dayjs from 'dayjs';
import { action, computed, makeObservable, runInAction } from 'mobx';

import { ENDPOINTS } from '@/configs/endpoints';
import { InsightEntityApi, InsightSchema } from '@/entities/Insights';
import type { NotificationsStore } from '@/stores/global/RootStore/NotificationsStore';
import { FormCreateNoteStore as InsightFormStore } from '@/stores/local/pages/OrganizationPageInsightCreateStore/FormCreateNoteStore';
import { LIFESPAN_OPTIONS } from '@/stores/local/pages/OrganizationPageInsightCreateStore/LifespanSelectStore/config';
import { MetaStore } from '@/stores/local/shared/MetaStore';
import { PopupStore } from '@/stores/local/shared/PopupStore';
import { SequentialFieldPrefillStore } from '@/stores/local/shared/SequentialFieldPrefillStore';
import call from '@/utils/api/api';
import { schemeParse } from '@/utils/schemeParse';

type InsightQuickCaptureStoreParams = {
  organizationId: string;
  notificationsStore: NotificationsStore;
};

const DEFAULT_LIFESPAN = LIFESPAN_OPTIONS[0];

export class InsightQuickCaptureStore extends PopupStore<null> {
  private readonly _organizationId: string;
  private readonly _notificationsStore: NotificationsStore;

  readonly form: InsightFormStore;
  readonly meta = new MetaStore();

  private readonly _titlePrefill: SequentialFieldPrefillStore;

  constructor({ organizationId, notificationsStore }: InsightQuickCaptureStoreParams) {
    super();
    this._organizationId = organizationId;
    this._notificationsStore = notificationsStore;
    this.form = new InsightFormStore({ organizationId });
    this._titlePrefill = new SequentialFieldPrefillStore({
      title: 'Инсайт',
      field: this.form.fieldsMap.title,
      valueFactory: ({ title }) => `${title} ${dayjs().format('DD.MM.YYYY HH:mm')}`,
    });

    makeObservable<InsightQuickCaptureStore>(this, {
      canSubmit: computed,
      isLoading: computed,
      openWithSelection: action.bound,
      submit: action.bound,
    });
  }

  get isLoading(): boolean {
    return this._titlePrefill.meta.isLoading;
  }

  get canSubmit(): boolean {
    return !this.meta.isLoading && this.form.canSubmit;
  }

  openWithSelection(selection: string) {
    this.meta.reset();
    this.form.reset();

    const normalizedSelection = selection.trim();

    if (normalizedSelection.length > 0) {
      this.form.fieldsMap.text.setValue(normalizedSelection, { noTouch: true });
      this.form.fieldsMap.text.unTouch();
    }

    this._ensureDefaultLifespan();
    this._titlePrefill.reset();
    this._titlePrefill.ensure();

    super.open();
  }

  close() {
    super.close();
    this.meta.reset();
    this.form.reset();
  }

  async submit() {
    if (this.meta.isLoading) {
      return;
    }

    if (!this.form.validateFields()) {
      return;
    }

    this.meta.setLoadedStartMeta();

    const response = await call<InsightEntityApi>({
      endpoint: ENDPOINTS.insights.create(this._organizationId),
      method: 'POST',
      data: this.form.serverFormat,
    });

    if (response.isError) {
      this._notificationsStore.showError(
        response.error.response?.data.message ?? 'Не удалось создать инсайт. Попробуйте еще раз',
      );
      this.meta.setLoadedErrorMeta();

      return;
    }

    const parsed = schemeParse(InsightSchema, response.data);

    if (!parsed.success) {
      this._notificationsStore.showError('Не удалось создать инсайт. Попробуйте еще раз');
      this.meta.setLoadedErrorMeta();

      return;
    }

    runInAction(() => {
      this.meta.setLoadedSuccessMeta();
      this.close();
    });

    this._notificationsStore.showSuccess('Инсайт создан');
  }

  private _ensureDefaultLifespan() {
    if (!DEFAULT_LIFESPAN || this.form.fieldsMap.lifespan.value.length > 0) {
      return;
    }

    this.form.fieldsMap.lifespan.setValue([DEFAULT_LIFESPAN], { noTouch: true });
    this.form.fieldsMap.lifespan.unTouch();
  }

  destroy(): void {
    this.form.destroy();
    this.meta.destroy();
    this._titlePrefill.destroy();
  }
}
